import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { Input, Card, Tag, Button, Form, message } from 'antd';
import { useAuthOrg } from '../context/AuthOrgContext';
import { ArrowLeftOutlined, IdcardOutlined, LockOutlined, MailOutlined, PhoneOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { getRoleLabel, getRoleTagColor } from '../utils/roles';
import authService from '../apiServices/authService';

interface ChangePasswordFormValues {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const UserDetails: React.FC = () => {
    const navigate = useNavigate();
    const { user, currentOrg, availableOrgs } = useAuthOrg();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordForm] = Form.useForm<ChangePasswordFormValues>();

    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg" onClick={() => navigate(`/settings/user-details`)}>{showChangePassword ? 'Change Password' : 'User Details'}</div>,
            },
        ]);
    }, [setBreadcrumbs, navigate, showChangePassword]);

    const handleChangePassword = async (values: ChangePasswordFormValues) => {
        try {
            setPasswordLoading(true);
            const response = await authService.changePassword(values);

            if (response.meta.status) {
                message.success(response.meta.message || 'Password changed successfully');
                passwordForm.resetFields();
                setShowChangePassword(false);
            } else {
                passwordForm.resetFields();
                message.error(response.meta.message || 'Failed to change password');
            }
        } catch (error: any) {
            passwordForm.resetFields();
            message.error(error.message || 'Failed to change password');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleShowChangePassword = () => {
        passwordForm.resetFields();
        setShowChangePassword(true);
    };

    const handleBackToProfile = () => {
        passwordForm.resetFields();
        setShowChangePassword(false);
    };

    if (!user) {
        return <div className="flex items-center justify-center h-screen">Loading...</div>;
    }

    if (showChangePassword) {
        return (
            <section className="res-px py-2">
                <div className="bg-white p-4 md:p-6 border rounded-md shadow-md">
                    <div className="mb-4 md:mb-6">
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={handleBackToProfile}
                            className="flex items-center"
                        >
                            Go Back to Profile
                        </Button>
                    </div>

                    <div className="max-w-xl">
                        <h1 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">Change Password</h1>

                        <Card className="shadow-sm" size="small">
                            <Form
                                form={passwordForm}
                                layout="vertical"
                                onFinish={handleChangePassword}
                            >
                                <Form.Item
                                    name="currentPassword"
                                    label="Current Password"
                                    rules={[{ required: true, message: 'Please enter your current password' }]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined />}
                                        placeholder="Enter current password"
                                        autoComplete="current-password"
                                        className="h-9 md:h-10 text-sm"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="newPassword"
                                    label="New Password"
                                    rules={[
                                        { required: true, message: 'Please enter your new password' },
                                        {
                                            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,20}$/,
                                            message:
                                                'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
                                        },
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined />}
                                        placeholder="Enter new password"
                                        autoComplete="new-password"
                                        className="h-9 md:h-10 text-sm"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    dependencies={['newPassword']}
                                    rules={[
                                        { required: true, message: 'Please confirm your new password' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('newPassword') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Confirm password does not match'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined />}
                                        placeholder="Confirm new password"
                                        autoComplete="new-password"
                                        className="h-9 md:h-10 text-sm"
                                    />
                                </Form.Item>

                                <div className="flex justify-end">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={passwordLoading}
                                        icon={<LockOutlined />}
                                        className="bg-[#1F344C]"
                                    >
                                        Change Password
                                    </Button>
                                </div>
                            </Form>
                        </Card>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="res-px py-2">
            <div className="bg-white p-4 md:p-6 border rounded-md shadow-md">
                <h1 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">Your Profile</h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Avatar and Basic Info */}
                    <div className="lg:col-span-1">
                        <div className="flex flex-col items-center">
                            <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-[#1F344C] to-[#33333C] rounded-full flex items-center justify-center text-3xl md:text-5xl text-white mb-4 shadow-lg">
                                {user.userName ? user.userName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                            </div>
                            <h2 className="text-lg md:text-xl font-semibold text-gray-800 text-center">{user.userName || user.username || 'User'}</h2>
                            <p className="text-gray-500 text-sm md:text-base text-center break-all">{user.email}</p>
                            {user.userRole && (
                                <Tag color={getRoleTagColor(user.userRole)} className="mt-2">
                                    {getRoleLabel(user.userRole)}
                                </Tag>
                            )}
                            {user?.userType && <Button
                                type="link"
                                icon={<LockOutlined />}
                                onClick={handleShowChangePassword}
                                className="mt-3 p-0 flex items-center"
                            >
                                Change Password
                            </Button>}
                        </div>
                    </div>

                    {/* Right Column - User Details */}
                    <div className="lg:col-span-2">
                        <Card title="Personal Information" className="mb-4 shadow-sm" size="small">
                            <div className="space-y-4">
                                <div className="flex flex-col">
                                    <label className="text-gray-500 text-xs md:text-sm mb-1 flex items-center gap-2">
                                        <UserOutlined /> Full Name
                                    </label>
                                    <Input
                                        value={user.userName || user.username || ''}
                                        className="h-9 md:h-10 text-sm"
                                        disabled
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <label className="text-gray-500 text-xs md:text-sm mb-1 flex items-center gap-2">
                                        <MailOutlined /> Email
                                    </label>
                                    <Input
                                        value={user.email}
                                        className="h-9 md:h-10 text-sm"
                                        disabled
                                    />
                                </div>

                                {user.userMobile && (
                                    <div className="flex flex-col">
                                        <label className="text-gray-500 text-xs md:text-sm mb-1 flex items-center gap-2">
                                            <PhoneOutlined /> Mobile
                                        </label>
                                        <Input
                                            value={user.userMobile}
                                            className="h-9 md:h-10 text-sm"
                                            disabled
                                        />
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {user.userMode && (
                                        <div className="flex flex-col">
                                            <label className="text-gray-500 text-xs md:text-sm mb-1 flex items-center gap-2">
                                                <IdcardOutlined /> User Mode
                                            </label>
                                            <Input
                                                value={user.userMode}
                                                className="h-9 md:h-10 text-sm"
                                                disabled
                                            />
                                        </div>
                                    )}

                                    {user.userType && (
                                        <div className="flex flex-col">
                                            <label className="text-gray-500 text-xs md:text-sm mb-1 flex items-center gap-2">
                                                <TeamOutlined /> User Type
                                            </label>
                                            <Input
                                                value={user.userType}
                                                className="h-9 md:h-10 text-sm"
                                                disabled
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* Accounts List */}
                        <Card title="Accessible Accounts" className="mt-4 shadow-sm" size="small">
                            <div className="space-y-3 h-44 overflow-auto">
                                {availableOrgs.length > 0 ? (
                                    availableOrgs.map((org) => (
                                        <div
                                            key={org.orgId}
                                            className={`p-3 md:p-4 border rounded-lg transition-all ${currentOrg?.orgId === org.orgId
                                                ? 'border-indigo-500 bg-indigo-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                                                <div className="overflow-hidden w-full">
                                                    <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm md:text-base">
                                                        <span className="truncate">{org.orgName}</span>
                                                        {currentOrg?.orgId === org.orgId && (
                                                            <Tag color="green" className="flex-shrink-0">Parent</Tag>
                                                        )}
                                                    </h3>
                                                    <p className="text-xs text-gray-500 mt-1 truncate">
                                                        ID: {org.orgId}
                                                    </p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    {org.orgType && (
                                                        <Tag
                                                            color={
                                                                org.orgType === 'parent'
                                                                    ? 'purple'
                                                                    : org.orgType === 'self_served'
                                                                        ? 'blue'
                                                                        : 'green'
                                                            }
                                                            className="m-0"
                                                        >
                                                            {org.orgType === 'parent'
                                                                ? 'Times Internet'
                                                                : org.orgType === 'self_served'
                                                                    ? 'Primary'
                                                                    : 'Managed'}
                                                        </Tag>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-gray-500 text-center py-4 text-sm">
                                        No accounts available
                                    </p>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserDetails;
